import { Box, CircularProgress, Typography } from "@mui/material";
import { FloatingPanel } from "components/common/FloatingPanel";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import {
  useAreaMaterialColors,
  useAreaMaterialLabels,
  useReportedRailcarMaterials,
} from "components/scrapEyeLabelling/hooks/useReportedRailcarMaterials";
import { RailCarStyleLayout } from "components/scrapEyeLayout/RailCarLayout";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { memo, useMemo } from "react";
import { useGetReportedRailcarSlotsQuery } from "src/store/api/generatedApi";

const NO_NON_SELECTABLE_AREAS: readonly string[] = [];
const PAGE_TOP_LEFT = { x: 16, y: 16 };
const MemoisedRailCarStyleLayout = memo(RailCarStyleLayout);

type ReportedRailcarMapProps = Readonly<{
  location: string;
  startIso: string;
  endIso: string;
  videoStart: Dayjs;
}>;

const ReportedRailcarMap = ({
  location,
  startIso,
  endIso,
  videoStart,
}: ReportedRailcarMapProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { default_area_materials: defaultAreaMaterials } =
    useScrapEyeMetadata();
  const {
    currentData: reportedSlots,
    isFetching,
    isError,
  } = useGetReportedRailcarSlotsQuery({
    tenantName,
    location,
    startTime: startIso,
    endTime: endIso,
    numbering: "plant",
  });
  const { currentTime } = useVideoControlState();
  const spanBoundariesMs = useMemo(
    () =>
      sorted(
        (reportedSlots?.spans ?? []).flatMap((span) => [
          dayjs(span.start).valueOf(),
          dayjs(span.end).valueOf(),
        ]),
        (boundaryMs) => boundaryMs
      ),
    [reportedSlots]
  );
  const playheadMs = videoStart.valueOf() + currentTime * 1000;
  const lastBoundaryMs = spanBoundariesMs
    .filter((boundaryMs) => boundaryMs <= playheadMs)
    .at(-1);
  // Moves only at span boundaries, so the memoised layout skips other ticks.
  const spanLookupTimestamp = useMemo(
    () => dayjs.utc(lastBoundaryMs ?? videoStart.valueOf()),
    [lastBoundaryMs, videoStart]
  );
  const reportedMaterials = useReportedRailcarMaterials(
    reportedSlots?.spans,
    spanLookupTimestamp
  );
  const areaMaterials = useAreaMaterialLabels(
    defaultAreaMaterials,
    reportedMaterials
  );
  const areaColors = useAreaMaterialColors(
    defaultAreaMaterials,
    reportedMaterials
  );

  if (isFetching && reportedSlots === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || reportedSlots === undefined) {
    return (
      <Typography variant="body1" sx={{ p: 4, textAlign: "center" }}>
        {t("failedToLoadData")}
      </Typography>
    );
  }
  return (
    <MemoisedRailCarStyleLayout
      layout={reportedSlots.layout.data}
      nonSelectableAreas={NO_NON_SELECTABLE_AREAS}
      areaColors={areaColors}
      areaMaterials={areaMaterials}
    />
  );
};

export const FloatingRailcarMap = ({
  onClose,
  ...mapProps
}: ReportedRailcarMapProps & Readonly<{ onClose: () => void }>) => {
  const { t } = useTenantTranslation();
  return (
    <FloatingPanel
      title={t("railcars")}
      initialPosition={PAGE_TOP_LEFT}
      onClose={onClose}
    >
      <ReportedRailcarMap {...mapProps} />
    </FloatingPanel>
  );
};
